import React from 'react';
import MyInputTextArea from '../../../../../components/MyInputTextArea';
import classNames from 'classnames';
import { type components, type paths } from '../../../../../endpoint/Schema';

const EmptyTextDetails: components['schemas']['InsightSettingsTextDto']['textDetails'] =
  {
    safeContent: {
      mainText: '',
      description: '',
    },
    alertContent: {
      mainText: '',
      description: '',
    },
    dangerContent: {
      mainText: '',
      description: '',
    },
  };

interface IInsightMessagesProps {
  title: string;
  description: string;
  safeMessagePlaceholder: string;
  safeHintPlaceholder: string;
  alertMessagePlaceholder: string;
  alertHintPlaceholder: string;
  dangerMessagePlaceholder: string;
  dangerHintPlaceholder: string;
  className?: string;
  insightSettingsText: Array<
    components['schemas']['InsightSettingsTextDto']
  > | null;
  insightType: components['schemas']['InsightSettingsTextEnum'];
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<
      | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
      | undefined
    >
  >;
}

export default function InsightMessages({
  title,
  description,
  safeMessagePlaceholder,
  safeHintPlaceholder,
  alertMessagePlaceholder,
  alertHintPlaceholder,
  dangerMessagePlaceholder,
  dangerHintPlaceholder,
  className,
  insightSettingsText,
  insightType,
  setFeatureSettings,
}: IInsightMessagesProps) {
  const texts = insightSettingsText?.find(
    (item) => item.textType === insightType,
  );

  const handleChangeText =
    (
      zone: keyof components['schemas']['InsightSettingsTextDetailsDto'],
      field: keyof components['schemas']['InsightSettingsTextContentDto'],
    ) =>
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFeatureSettings((prev) => {
        const currentTexts = [
          ...(prev?.insightSettings?.insightSettingsText ?? []),
        ];
        const index = currentTexts.findIndex(
          (item) => item.textType === insightType,
        );
        const newText: components['schemas']['InsightSettingsTextDto'] = {
          textType: insightType,
          textDetails: {
            ...(texts?.textDetails ?? EmptyTextDetails),
            [zone]: {
              mainText: '',
              description: '',
              ...texts?.textDetails?.[zone],
              [field]: e.target.value,
            },
          },
        };
        if (index > -1) currentTexts[index] = newText;
        else currentTexts.push(newText);

        return {
          ...prev,
          insightSettings: {
            ...prev?.insightSettings,
            insightSettingsText: currentTexts,
          },
        };
      });
    };

  return (
    <div className={classNames('w-full', className)}>
      <div className="flex flex-row items-center">
        <div>{title}</div>
        <div className="shrink-0 h-px flex-1 ml-2 border-b border-solid bg-zinc-300 border-zinc-300 max-md:max-w-full" />
      </div>

      <div className="w-full px-20 text-xl text-[color:var(--color-stormGray)]">
        <div className="mt-4">{description}</div>

        <div className="flex flex-row p-5 mt-6 gap-5 rounded-xl bg-neutral-100">
          <div className="flex flex-1 flex-col">
            <MyInputTextArea
              label="Safe main message"
              value={texts?.textDetails?.safeContent?.mainText ?? ''}
              onChange={handleChangeText('safeContent', 'mainText')}
              placeholder={safeMessagePlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
            <MyInputTextArea
              label="Alert main message"
              value={texts?.textDetails?.alertContent?.mainText ?? ''}
              onChange={handleChangeText('alertContent', 'mainText')}
              placeholder={alertMessagePlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
            <MyInputTextArea
              label="Danger main message"
              value={texts?.textDetails?.dangerContent?.mainText ?? ''}
              onChange={handleChangeText('dangerContent', 'mainText')}
              placeholder={dangerMessagePlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
          </div>

          <div className="flex flex-1 flex-col">
            <MyInputTextArea
              label="Safe hint"
              value={texts?.textDetails?.safeContent?.description ?? ''}
              onChange={handleChangeText('safeContent', 'description')}
              placeholder={safeHintPlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
            <MyInputTextArea
              label="Alert hint"
              value={texts?.textDetails?.alertContent?.description ?? ''}
              onChange={handleChangeText('alertContent', 'description')}
              placeholder={alertHintPlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
            <MyInputTextArea
              label="Danger hint"
              value={texts?.textDetails?.dangerContent?.description ?? ''}
              onChange={handleChangeText('dangerContent', 'description')}
              placeholder={dangerHintPlaceholder}
              inputClassName="flex flex-1 text-xl text-black"
              className="my-2"
              rows={4}
              autoResize
              maxLength={100}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
